import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { AdminLayout } from "../../layout/AdminLayout";
import { contactsService } from "../../../services/contactsService";

export const ContactView = () => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    loadContact();
  }, [id]);

  const loadContact = async () => {
    try {
      setLoading(true);
      const data = await contactsService.getById(id);
      setContact(data || null);
    } catch {
      toast.error("Failed to load contact entry");
      navigate("/admin/contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    if (
      !window.confirm("Are you sure you want to delete this contact entry?")
    ) {
      return;
    }

    try {
      setDeleting(true);
      await contactsService.delete(contact._id);
      toast.success("Contact entry deleted");
      navigate("/admin/contacts");
    } catch {
      toast.error("Failed to delete contact entry");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Link
                to="/admin/contacts"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
              >
                <ArrowLeft size={18} />
                Back to Contacts
              </Link>
            </div>

            {contact && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 text-white px-4 py-2 rounded-lg transition"
              >
                <Trash2 size={18} />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            {loading ? (
              <p className="text-gray-500">Loading contact entry...</p>
            ) : !contact ? (
              <p className="text-gray-500">Contact entry not found.</p>
            ) : (
              <div className="space-y-6">
                <h1 className="text-3xl font-bold text-gray-800">
                  Contact Entry
                </h1>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">
                      Name
                    </label>
                    <p className="text-gray-900">{contact.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">
                      Email
                    </label>
                    <p className="text-gray-900">{contact.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">
                      Company
                    </label>
                    <p className="text-gray-900">{contact.company}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">
                      Submitted At
                    </label>
                    <p className="text-gray-900">
                      {contact.createdAt
                        ? new Date(contact.createdAt).toLocaleString()
                        : "-"}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Message
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 whitespace-pre-wrap text-gray-900">
                    {contact.message}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
